/* ============================================================
   Membership Gift Card — Product Info section
   Two editor-resilient custom elements:
   - <co-giftcard-gallery>  main image + thumbnails + prev/next
   - <co-giftcard-buy>      keeps the hidden selling_plan in sync with the
                            selected membership variant and reveals the matching
                            description in the details dropdown
   ============================================================ */
(function () {
  if (!window.customElements) return;

  var DEC_SELECTOR =
    '.co-product-dec-1, .co-product-dec-2, .co-product-dec-3, .co-product-dec-4, .co-product-dec-5, .co-product-dec-6';

  /* ---------------------- GSAP readiness ----------------------
     This file is rendered inside the sections, so it executes BEFORE the
     deferred GSAP bundles in co-embed-scripts (rendered near the end of
     theme.liquid). A one-shot `if (!window.gsap)` check would therefore lose
     the race and permanently set opacity:1 inline — content visible, nothing
     animating. Poll instead, and only give up (revealing content outright)
     once GSAP genuinely isn't coming. */
  function pollFor(test, callback) {
    var waited = 0;
    var step = 50;
    var limit = 5000;
    (function check() {
      if (test()) return callback(true);
      if (waited >= limit) return callback(false);
      waited += step;
      setTimeout(check, step);
    })();
  }

  function whenGsapReady(callback) {
    pollFor(function () {
      return window.gsap && window.ScrollTrigger;
    }, callback);
  }

  /* Flickity ships in the same deferred bundle as GSAP, so it loses the same
     race against this file. Poll for it rather than bailing out. */
  function whenFlickityReady(callback) {
    pollFor(function () {
      return window.Flickity;
    }, callback);
  }

  function showOutright(selector) {
    document.querySelectorAll(selector).forEach(function (el) {
      el.style.opacity = 1;
    });
  }

  /* ScrollTrigger measures start/end positions when a trigger is created, but
     this page keeps settling afterwards (lazy images, web fonts, the large
     inline mountain SVG). Without a refresh, triggers can read as already
     passed and snap to their end state with no visible animation. */
  function refreshTriggersWhenSettled() {
    var refresh = function () {
      if (window.ScrollTrigger) ScrollTrigger.refresh();
    };
    if (document.readyState === 'complete') {
      refresh();
    } else {
      window.addEventListener('load', refresh, { once: true });
    }
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(refresh);
    }
  }

  /* ---------------------- Stock dec-section reveals ----------------------
     The co-product-dec-* sections hide themselves with an inline
     `html:not(.shopify-design-mode) .co-product-dec-N { opacity: 0 }` and are
     revealed by co-scripts-product-dec.js — which co-embed-scripts only loads
     on the standard product templates, NOT product.membership_giftcard (it
     gets co-scripts-product.js instead, which lacks the reveals). Replicate
     them here for any dec section used on this template. */
  function revealDecSections() {
    document.querySelectorAll(DEC_SELECTOR).forEach(function (el) {
      if (el.dataset.coGcRevealBound) return;
      el.dataset.coGcRevealBound = 'true';
      gsap.to(el, {
        scrollTrigger: { trigger: el, start: 'top 90%', end: 'bottom top' },
        opacity: 1,
        duration: 1,
        ease: 'Power2.inOut'
      });
    });
  }

  /* ---------------------- Gift card section animations ----------------------
     Entrance animations for the custom gift-card sections, following the
     theme's GSAP conventions. Initial opacity:0 for .co-gc-ani elements lives
     in each section's stylesheet (skipped in the theme editor); if GSAP is
     unavailable or the visitor prefers reduced motion, everything is revealed
     outright so content can never stay hidden. Idempotent per section, so the
     shared script tag rendered by several sections is safe. */
  function buildGiftcardAnimations() {
    // Product info: gallery + staggered buy column on page load (above the fold)
    var gc = document.querySelector('.co-gc');
    if (gc && !gc.dataset.coGcAnimated) {
      gc.dataset.coGcAnimated = 'true';
      var media = gc.querySelector('.co-gc-media');
      var infoItems = gc.querySelectorAll('.co-gc-info .co-gc-ani');
      if (media) {
        gsap.fromTo(media, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' });
      }
      if (infoItems.length) {
        gsap.fromTo(infoItems, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out', stagger: 0.08, delay: 0.2 });
      }
    }

    // Delivered: illustration settles, copy rises
    var delivered = document.querySelector('.co-gc-delivered');
    if (delivered && !delivered.dataset.coGcAnimated) {
      delivered.dataset.coGcAnimated = 'true';
      var dMedia = delivered.querySelector('.co-gc-delivered-media');
      var dContent = delivered.querySelector('.co-gc-delivered-content');
      if (dMedia) {
        gsap.fromTo(dMedia, { opacity: 0, scale: 1.04 }, { opacity: 1, scale: 1, duration: 0.9, ease: 'power2.out', scrollTrigger: { trigger: delivered, start: 'top 80%' } });
      }
      if (dContent) {
        gsap.fromTo(dContent, { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 0.9, ease: 'power2.out', delay: 0.15, scrollTrigger: { trigger: delivered, start: 'top 80%' } });
      }
    }

    // Perks: heading first, then the grid cells cascade in
    var perks = document.querySelector('.co-gc-perks');
    if (perks && !perks.dataset.coGcAnimated) {
      perks.dataset.coGcAnimated = 'true';
      var pTitle = perks.querySelector('.co-gc-perks-title');
      var pCells = perks.querySelectorAll('.co-gc-perk');
      if (pTitle) {
        gsap.fromTo(pTitle, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out', scrollTrigger: { trigger: perks, start: 'top 75%' } });
      }
      if (pCells.length) {
        gsap.fromTo(pCells, { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 0.7, ease: 'power2.out', stagger: 0.08, delay: 0.1, scrollTrigger: { trigger: perks, start: 'top 75%' } });
      }
    }

    // Social proof: heading then the photo strip
    var social = document.querySelector('.co-gc-social');
    if (social && !social.dataset.coGcAnimated) {
      social.dataset.coGcAnimated = 'true';
      var sItems = social.querySelectorAll('.co-gc-ani');
      if (sItems.length) {
        gsap.fromTo(sItems, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out', stagger: 0.12, scrollTrigger: { trigger: social, start: 'top 80%' } });
      }
    }

    // Limited edition: copy fades up, and the sun rises from behind the ridge
    // as the scene scrolls into view (scrub-linked). The svg (not the wrapper)
    // is animated so the wrapper's centering transform is left alone.
    var limited = document.querySelector('.co-gc-limited');
    if (limited && !limited.dataset.coGcAnimated) {
      limited.dataset.coGcAnimated = 'true';
      var lItems = limited.querySelectorAll('.co-gc-limited-content .co-gc-ani');
      var scene = limited.querySelector('.co-gc-limited-scene');
      var sunSvg = limited.querySelector('.co-gc-limited-sun .co-gc-sun-svg');
      if (lItems.length) {
        gsap.fromTo(lItems, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out', stagger: 0.1, scrollTrigger: { trigger: limited, start: 'top 75%' } });
      }
      if (sunSvg && scene) {
        gsap.fromTo(sunSvg, { yPercent: 45 }, {
          yPercent: 0,
          ease: 'none',
          scrollTrigger: { trigger: scene, start: 'top 90%', end: 'top 35%', scrub: true }
        });
      }
    }
  }
  function startAnimations() {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var designMode = document.documentElement.classList.contains('shopify-design-mode');
    if (reduced || designMode) {
      showOutright('.co-gc-ani');
      showOutright(DEC_SELECTOR);
      return;
    }
    whenGsapReady(function (ready) {
      if (!ready) {
        // GSAP never arrived — never leave content hidden.
        showOutright('.co-gc-ani');
        showOutright(DEC_SELECTOR);
        return;
      }
      revealDecSections();
      buildGiftcardAnimations();
      refreshTriggersWhenSettled();
    });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startAnimations);
  } else {
    startAnimations();
  }

  /* ------------------------ Social proof carousel ------------------------
     Flickity strip of UGC / influencer photos. Editor-resilient: initialises on
     connect and destroys on disconnect so theme-editor re-renders don't leave
     orphaned instances behind. */
  if (!customElements.get('co-giftcard-social')) {
    customElements.define(
      'co-giftcard-social',
      class CoGiftcardSocial extends HTMLElement {
        connectedCallback() {
          if (this._ready) return;
          this.slider = this.querySelector('[data-gc-social-slider]');
          if (!this.slider) return;

          var cellCount = this.slider.children.length;
          if (!cellCount) return;
          this._ready = true;

          var self = this;
          whenFlickityReady(function (ready) {
            // Element may have been torn down while we waited.
            if (!ready || !self.isConnected || self.flkty) return;

            var reduced =
              window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            var autoplay = self.dataset.autoplay === 'true' && !reduced;
            var interval = parseInt(self.dataset.autoplayInterval, 10) || 5000;

            // wrapAround only looks right once the cells comfortably exceed the
            // viewport width; below that Flickity leaves gaps at the seam (the
            // same failure the dec-5 carousel hit), so fall back to a contained
            // left-aligned slider.
            var canWrap = cellCount >= 5;

            self.flkty = new Flickity(self.slider, {
              pageDots: false,
              prevNextButtons: false,
              draggable: cellCount > 1,
              wrapAround: canWrap,
              contain: !canWrap,
              cellAlign: canWrap ? 'center' : 'left',
              imagesLoaded: true,
              autoPlay: autoplay ? interval : false
            });

            /* Video slides: force muted + inline playback (iOS goes fullscreen
               without playsinline), and only play while the section is actually
               on screen so a strip of clips doesn't chew bandwidth or battery.
               Reduced-motion visitors get a paused poster frame. */
            self.videos = Array.prototype.slice.call(self.querySelectorAll('video'));
            if (self.videos.length) {
              self.videos.forEach(function (video) {
                video.muted = true;
                video.loop = true;
                video.playsInline = true;
                video.setAttribute('playsinline', '');
                video.setAttribute('preload', 'metadata');
                if (reduced) {
                  video.autoplay = false;
                  video.removeAttribute('autoplay');
                  try {
                    video.pause();
                  } catch (e) {
                    /* not ready yet */
                  }
                }
              });

              if (!reduced && window.IntersectionObserver) {
                self._videoObserver = new IntersectionObserver(
                  function (entries) {
                    entries.forEach(function (entry) {
                      var video = entry.target;
                      if (entry.isIntersecting) {
                        var playing = video.play();
                        // Autoplay can still be refused (low power mode) — ignore.
                        if (playing && playing.catch) playing.catch(function () {});
                      } else {
                        try {
                          video.pause();
                        } catch (e) {
                          /* already gone */
                        }
                      }
                    });
                  },
                  { threshold: 0.25 }
                );
                self.videos.forEach(function (video) {
                  self._videoObserver.observe(video);
                });
              }
            }

          });
        }

        disconnectedCallback() {
          if (this._videoObserver) {
            this._videoObserver.disconnect();
            this._videoObserver = null;
          }
          if (this.flkty) {
            try {
              this.flkty.destroy();
            } catch (e) {
              /* already gone */
            }
            this.flkty = null;
          }
          this._ready = false;
        }
      }
    );
  }

  /* ---------------------------- Gallery ---------------------------- */
  if (!customElements.get('co-giftcard-gallery')) {
    customElements.define(
      'co-giftcard-gallery',
      class CoGiftcardGallery extends HTMLElement {
        connectedCallback() {
          if (this._ready) return;
          this.slides = Array.from(this.querySelectorAll('[data-gc-slide]'));
          this.thumbs = Array.from(this.querySelectorAll('[data-gc-thumb]'));
          this.prevBtn = this.querySelector('[data-gc-prev]');
          this.nextBtn = this.querySelector('[data-gc-next]');
          if (this.slides.length <= 1) {
            this._ready = true;
            return;
          }

          this.index = this.slides.findIndex((s) => s.classList.contains('is-active'));
          if (this.index < 0) this.index = 0;

          this._onThumb = [];
          this.thumbs.forEach((thumb, i) => {
            const handler = () => this.setActive(i);
            thumb.addEventListener('click', handler);
            this._onThumb.push(handler);
          });

          this._onPrev = () => this.setActive(this.index - 1);
          this._onNext = () => this.setActive(this.index + 1);
          if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrev);
          if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNext);

          this._ready = true;
        }

        disconnectedCallback() {
          if (this.thumbs && this._onThumb) {
            this.thumbs.forEach((thumb, i) => thumb.removeEventListener('click', this._onThumb[i]));
          }
          if (this.prevBtn && this._onPrev) this.prevBtn.removeEventListener('click', this._onPrev);
          if (this.nextBtn && this._onNext) this.nextBtn.removeEventListener('click', this._onNext);
          this._ready = false;
        }

        setActive(i) {
          const total = this.slides.length;
          const next = ((i % total) + total) % total; // wrap-around
          this.index = next;

          this.slides.forEach((slide, idx) => {
            const active = idx === next;
            slide.classList.toggle('is-active', active);
            slide.toggleAttribute('aria-hidden', !active);
          });

          this.thumbs.forEach((thumb, idx) => {
            const active = idx === next;
            thumb.classList.toggle('is-active', active);
            if (active) {
              thumb.setAttribute('aria-current', 'true');
              if (thumb.scrollIntoView) {
                thumb.scrollIntoView({ block: 'nearest', inline: 'nearest' });
              }
            } else {
              thumb.removeAttribute('aria-current');
            }
          });
        }
      }
    );
  }

  /* ---------------------------- Buy area ---------------------------- */
  if (!customElements.get('co-giftcard-buy')) {
    customElements.define(
      'co-giftcard-buy',
      class CoGiftcardBuy extends HTMLElement {
        connectedCallback() {
          if (this._ready) return;
          this.planInput = this.querySelector('.co-gc-selling-plan');
          this.radios = Array.from(this.querySelectorAll('.co-gc-option-input'));
          this.recipient = this.querySelector('recipient-form');
          this.submitButton = this.querySelector('.co-gc-submit');
          if (!this.radios.length) {
            this._ready = true;
            return;
          }

          // product-form.js force-enables the first [name=id] input in its
          // constructor; re-assert disabled on any sold-out tier so it can't be
          // reached by keyboard. The submit-button gate in select() is the
          // authoritative guard if the editor re-renders in a different order.
          this.radios.forEach((r) => {
            if (r.dataset.available === 'false') r.disabled = true;
          });

          this._onChange = [];
          this.radios.forEach((radio) => {
            const handler = () => {
              if (radio.checked) this.select(radio);
            };
            radio.addEventListener('change', handler);
            this._onChange.push({ radio, handler });
          });

          const checked = this.radios.find((r) => r.checked) || this.radios[0];
          this.select(checked);
          this._ready = true;
        }

        disconnectedCallback() {
          if (this._onChange) {
            this._onChange.forEach(({ radio, handler }) => radio.removeEventListener('change', handler));
          }
          this._ready = false;
        }

        select(radio) {
          // Keep the hidden selling_plan in sync. Disabled inputs are not
          // submitted, so a variant with no plan falls back to a one-time add
          // rather than posting an empty selling_plan (which /cart/add rejects).
          if (this.planInput) {
            const planId = radio.getAttribute('data-selling-plan') || '';
            this.planInput.value = planId;
            this.planInput.disabled = planId === '';
          }

          // Gate the submit button on the selected tier's availability so a
          // sold-out variant can never be added (which would 422 silently when
          // errors are hidden by the gift-card recipient form).
          if (this.submitButton) {
            this.submitButton.disabled = radio.dataset.available === 'false';
          }

          // Point the gift-card recipient form at the selected variant. Setting
          // the attribute alone is a no-op — recipient-form.js reads it once in
          // its constructor — so update the live property too, which its reset
          // and error handlers compare against (recipient-form.js).
          if (this.recipient) {
            const variantId = radio.getAttribute('data-variant-id');
            if (variantId) {
              this.recipient.setAttribute('data-product-variant-id', variantId);
              this.recipient.currentProductVariantId = String(variantId);
            }
          }
        }
      }
    );
  }
})();
